import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Protocolos.css';

const Protocolos = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Debug: Log inicial
  if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Protocolos component montado:', {
      pathname: location?.pathname || 'N/A',
      user: user?.nome_completo
    });
  }
  
  // Determinar aba ativa baseado na rota
  const [abaAtiva, setAbaAtiva] = useState(
    location?.pathname?.includes('/enviados') ? 'enviados' : 'recebidos'
  );
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDocumento, setSelectedDocumento] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [historicoLeituras, setHistoricoLeituras] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showModalCiencia, setShowModalCiencia] = useState(false);
  const [tramitacaoCiencia, setTramitacaoCiencia] = useState(null);
  const [certificadoCiencia, setCertificadoCiencia] = useState(null);
  const [salvandoCiencia, setSalvandoCiencia] = useState(false);
  const [anexoVisualizando, setAnexoVisualizando] = useState(null);
  const [urlVisualizacao, setUrlVisualizacao] = useState(null);
  const [devolutiva, setDevolutiva] = useState('');
  const [salvandoDevolutiva, setSalvandoDevolutiva] = useState(false);
  const [usarCertificacaoDevolutiva, setUsarCertificacaoDevolutiva] = useState(false);
  const [showModalEncaminhar, setShowModalEncaminhar] = useState(false);
  const [showModalUpload, setShowModalUpload] = useState(false);
  const [destinos, setDestinos] = useState([]);
  const [destinoSelecionado, setDestinoSelecionado] = useState(null);
  const [observacoesEncaminhar, setObservacoesEncaminhar] = useState('');
  const [salvandoEncaminhar, setSalvandoEncaminhar] = useState(false);
  const [arquivosSelecionados, setArquivosSelecionados] = useState([]);
  const [enviandoArquivos, setEnviandoArquivos] = useState(false);
  const [versoesAnexo, setVersoesAnexo] = useState([]);
  const [mostrarVersoes, setMostrarVersoes] = useState(false);
  const [anexoSelecionadoVersoes, setAnexoSelecionadoVersoes] = useState(null);
  const [workflowInstancia, setWorkflowInstancia] = useState(null);

  // Atualizar aba quando a rota mudar
  useEffect(() => {
    // Verificar se location existe
    if (!location || !location.pathname) {
      return;
    }
    
    try {
      const path = location.pathname;
      let novaAba = 'recebidos'; // padrão
      
      // Detectar se é enviados ou recebidos baseado na rota
      if (path && typeof path === 'string' && path.includes('/enviados')) {
        novaAba = 'enviados';
      } else if (path === '/documentos' || (path && typeof path === 'string' && path.startsWith('/documentos') && !path.includes('/enviados') && !path.includes('/criar'))) {
        novaAba = 'recebidos';
      }
      
      // Debug
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Rota mudou:', { path, novaAba, abaAtivaAtual: abaAtiva });
      }
      
      // Sempre atualizar a aba quando a rota mudar
      if (novaAba !== abaAtiva) {
        setAbaAtiva(novaAba);
      }
    } catch (error) {
      // Em caso de erro, manter aba atual
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro ao atualizar aba:', error);
      }
    }
  }, [location?.pathname, abaAtiva]);

  // Garantir que documentos sempre seja um array
  useEffect(() => {
    if (!Array.isArray(documentos)) {
      setDocumentos([]);
    }
  }, [documentos]);

  // Carregar documentos quando a aba mudar ou quando o componente montar
  useEffect(() => {
    // Debug
    if (process.env.NODE_ENV === 'development') {
      console.log('📥 Carregando documentos para aba:', abaAtiva);
    }
    
    // Limpar documentos anteriores para evitar mostrar dados errados
    setDocumentos([]);
    setLoading(true);
    setError('');
    
    if (abaAtiva === 'recebidos') {
      carregarDocumentosRecebidos();
    } else if (abaAtiva === 'enviados') {
      carregarDocumentosEnviados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaAtiva]);

  // Recarregar quando a página receber foco (para atualizar após criar documento)
  useEffect(() => {
    const handleFocus = () => {
      if (abaAtiva === 'recebidos') {
        carregarDocumentosRecebidos();
      } else {
        carregarDocumentosEnviados();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [abaAtiva]);

  const carregarDocumentosRecebidos = async () => {
    try {
      setLoading(true);
      setError('');
      setDocumentos([]); // Limpar documentos anteriores
      
      // Debug
      if (process.env.NODE_ENV === 'development') {
        console.log('📥 Fazendo requisição para /protocolos/recebidos');
      }
      
      const response = await api.get('/protocolos/recebidos?limit=100');
      
      // Debug
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Resposta recebida:', {
          success: response.data?.success,
          dataLength: response.data?.data?.length || 0,
          data: response.data
        });
      }
      
      if (response.data && response.data.success) {
        const docs = response.data.data;
        // Garantir que docs seja um array
        const documentosArray = Array.isArray(docs) ? docs : [];
        setDocumentos(documentosArray);
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Documentos recebidos carregados:', documentosArray.length);
        }
      } else {
        const errorMsg = response.data?.message || 'Erro ao carregar documentos recebidos';
        setError(errorMsg);
        setDocumentos([]);
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ Erro na resposta:', response.data);
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Erro ao carregar documentos recebidos';
      setError(errorMsg);
      setDocumentos([]);
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ Erro ao carregar recebidos:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
      }
    } finally {
      setLoading(false);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Loading finalizado para recebidos');
      }
    }
  };

  const carregarDocumentosEnviados = async () => {
    try {
      setLoading(true);
      setError('');
      setDocumentos([]); // Limpar documentos anteriores
      
      // Debug
      if (process.env.NODE_ENV === 'development') {
        console.log('📤 Fazendo requisição para /protocolos/enviados');
      }
      
      const response = await api.get('/protocolos/enviados?limit=100');
      
      // Debug
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Resposta recebida:', {
          success: response.data?.success,
          dataLength: response.data?.data?.length || 0,
          data: response.data
        });
      }
      
      if (response.data && response.data.success) {
        const docs = response.data.data;
        // Garantir que docs seja um array
        const documentosArray = Array.isArray(docs) ? docs : [];
        setDocumentos(documentosArray);
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Documentos enviados carregados:', documentosArray.length);
        }
      } else {
        const errorMsg = response.data?.message || 'Erro ao carregar documentos enviados';
        setError(errorMsg);
        setDocumentos([]);
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ Erro na resposta:', response.data);
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Erro ao carregar documentos enviados';
      setError(errorMsg);
      setDocumentos([]);
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ Erro ao carregar enviados:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
      }
    } finally {
      setLoading(false);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Loading finalizado para enviados');
      }
    }
  };

  const abrirDocumento = async (documento) => {
    try {
      setError(''); // Limpar erros anteriores
      
      // Verificar se tem id_protocolo
      if (!documento.id_protocolo) {
        setError('Documento sem ID de protocolo válido');
        // Log apenas em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          console.error('Documento sem id_protocolo:', documento);
        }
        return;
      }

      // Para documentos recebidos, verificar se precisa dar ciência (assinatura digital)
      if (abaAtiva === 'recebidos' && documento.id_tramitacao && !documento.teve_ciencia) {
        // Buscar certificado do usuário
        try {
          const certResponse = await api.get('/certificados/meu-certificado');
          if (certResponse.data.success && certResponse.data.data && certResponse.data.data.certificado) {
            const certificado = certResponse.data.data.certificado;
            // Verificar se o certificado é válido
            if (certResponse.data.data.valido && certificado.status === 'ativo') {
              setCertificadoCiencia(certificado);
              setTramitacaoCiencia(documento);
              setShowModalCiencia(true);
              return; // Não abrir ainda, aguardar ciência
            } else {
              setError('Seu certificado digital está expirado ou inválido. Acesse "Meu Certificado" para emitir um novo.');
              return;
            }
          } else {
            setError('É necessário ter um certificado digital válido para dar ciência do documento. Acesse "Meu Certificado" para emitir um.');
            return;
          }
        } catch (certError) {
          // Tratar erro 404 (certificado não encontrado) de forma diferente
          if (certError.response?.status === 404) {
            setError('É necessário ter um certificado digital válido para dar ciência do documento. Acesse "Meu Certificado" para emitir um.');
          } else {
            setError('Erro ao buscar certificado. É necessário ter um certificado digital válido para dar ciência do documento.');
          }
          return;
        }
      }

      // Marcar como lido se ainda não foi (apenas para recebidos)
      // Status muda para 'recebido' e ícone muda para aberto
      if (abaAtiva === 'recebidos' && !documento.lido && documento.id_tramitacao) {
        try {
          await api.put(`/protocolos/recebidos/${documento.id_tramitacao}/marcar-lido`);
          // Atualizar localmente: documento foi lido, então está aberto
          setDocumentos(prev => prev.map(doc => 
            doc.id_tramitacao === documento.id_tramitacao 
              ? { ...doc, lido: true, status_tramitacao: 'recebido', fechado: false, aberto: true }
              : doc
          ));
        } catch (marcarError) {
          // Log apenas em desenvolvimento
          if (process.env.NODE_ENV === 'development') {
            console.warn('Erro ao marcar como lido (continuando):', marcarError);
          }
          // Continuar mesmo se falhar
        }
      }

      // Registrar abertura do protocolo (histórico)
      try {
        await api.post(`/protocolos/${documento.id_protocolo}/abrir`, {
          id_tramitacao: documento.id_tramitacao || null
        });
        // Atualizar estado local: documento foi aberto (já deve estar aberto se foi lido)
        if (abaAtiva === 'recebidos' && documento.id_tramitacao) {
          setDocumentos(prev => prev.map(doc => 
            doc.id_tramitacao === documento.id_tramitacao 
              ? { ...doc, fechado: false, aberto: true, lido: true }
              : doc
          ));
        }
      } catch (abrirError) {
        // Verificar se é erro de ciência necessária
        if (abrirError.response?.data?.requer_ciencia) {
          // Buscar certificado e mostrar modal de ciência
          try {
            const certResponse = await api.get('/certificados/meu-certificado');
            if (certResponse.data.success && certResponse.data.data && certResponse.data.data.certificado) {
              const certificado = certResponse.data.data.certificado;
              // Verificar se o certificado é válido
              if (certResponse.data.data.valido && certificado.status === 'ativo') {
                setCertificadoCiencia(certificado);
                setTramitacaoCiencia(documento);
                setShowModalCiencia(true);
                return;
              } else {
                setError('Seu certificado digital está expirado ou inválido. Acesse "Meu Certificado" para emitir um novo.');
                return;
              }
            } else {
              setError('É necessário ter um certificado digital válido para dar ciência do documento.');
              return;
            }
          } catch (certError) {
            // Tratar erro 404 (certificado não encontrado) de forma diferente
            if (certError.response?.status === 404) {
              setError('É necessário ter um certificado digital válido para dar ciência do documento. Acesse "Meu Certificado" para emitir um.');
            } else {
              setError('Erro ao buscar certificado. É necessário ter um certificado digital válido para dar ciência do documento.');
            }
            return;
          }
        }
        // Log apenas em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          console.warn('Erro ao registrar abertura (continuando):', abrirError);
        }
        // Continuar mesmo se falhar
      }

      // Buscar detalhes completos do protocolo com histórico
      const [protocoloResponse, historicoResponse, leiturasResponse] = await Promise.all([
        api.get(`/protocolos/${documento.id_protocolo}`),
        api.get(`/protocolos/${documento.id_protocolo}/historico-aberturas`).catch(() => ({ data: { success: true, data: [] } })),
        api.get(`/protocolos/${documento.id_protocolo}/leituras`).catch(() => ({ data: { success: true, data: [] } }))
      ]);
      
      if (protocoloResponse.data && protocoloResponse.data.success) {
        const protocoloCompleto = protocoloResponse.data.data;
        // Buscar resposta da tramitação se for recebido
        if (abaAtiva === 'recebidos' && documento.id_tramitacao) {
          const tramitacaoCompleta = protocoloCompleto.tramitacoes?.find(t => t.id_tramitacao === documento.id_tramitacao);
          if (tramitacaoCompleta) {
            protocoloCompleto.resposta = tramitacaoCompleta.resposta;
            protocoloCompleto.id_tramitacao = documento.id_tramitacao;
            protocoloCompleto.teve_ciencia = tramitacaoCompleta.teve_ciencia || documento.teve_ciencia || false;
            protocoloCompleto.data_ciencia = tramitacaoCompleta.data_ciencia || documento.data_ciencia || null;
            protocoloCompleto.assinado_digitalmente = tramitacaoCompleta.assinado_digitalmente || documento.assinado_digitalmente || false;
            // Usar origem da tramitação se disponível (para documentos recebidos)
            // Prioridade: tramitação > documento original > protocolo
            if (tramitacaoCompleta.setorOrigem) {
              protocoloCompleto.setorOrigem = tramitacaoCompleta.setorOrigem;
            }
            if (tramitacaoCompleta.usuarioOrigem) {
              protocoloCompleto.usuarioOrigem = tramitacaoCompleta.usuarioOrigem;
            }
          }
          // Se não encontrou na tramitação, usar a origem do documento recebido original
          if (!protocoloCompleto.setorOrigem) {
            if (documento.setor_origem) {
              protocoloCompleto.setorOrigem = documento.setor_origem;
            } else if (documento.protocolo?.setorOrigem) {
              protocoloCompleto.setorOrigem = documento.protocolo.setorOrigem;
            }
          }
          if (!protocoloCompleto.usuarioOrigem) {
            if (documento.usuario_origem) {
              protocoloCompleto.usuarioOrigem = documento.usuario_origem;
            } else if (documento.protocolo?.usuarioOrigem) {
              protocoloCompleto.usuarioOrigem = documento.protocolo.usuarioOrigem;
            }
          }
        }
        setSelectedDocumento(protocoloCompleto);
        setHistorico(protocoloCompleto.tramitacoes || []);
        
        // Adicionar histórico de aberturas
        if (historicoResponse.data && historicoResponse.data.success) {
          setHistorico(prev => [
            ...prev,
            ...historicoResponse.data.data.map(abertura => ({
              tipo: 'abertura',
              acao: abertura.acao,
              usuario: abertura.usuario,
              data: abertura.data_acao,
              observacoes: abertura.observacoes
            }))
          ]);
        }
        
        // Adicionar histórico de leituras
        if (leiturasResponse && leiturasResponse.data && leiturasResponse.data.success) {
          setHistoricoLeituras(leiturasResponse.data.data);
        }
        
        setShowModal(true);
      } else {
        const errorMsg = protocoloResponse.data?.message || 'Erro ao carregar detalhes do documento';
        setError(errorMsg);
        // Log apenas em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          console.error('Resposta da API:', protocoloResponse.data);
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Erro ao abrir documento';
      setError(errorMsg);
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro completo ao abrir documento:', {
          error: err,
          response: err.response?.data,
          documento: documento
        });
      }
    }
  };

  const darCiencia = async () => {
    if (!tramitacaoCiencia || !certificadoCiencia) {
      setError('Dados insuficientes para dar ciência');
      return;
    }

    try {
      setSalvandoCiencia(true);
      setError('');
      
      await api.post(`/protocolos/recebidos/${tramitacaoCiencia.id_tramitacao}/ciencia`, {
        id_certificado: certificadoCiencia.id_certificado
      });

      // Atualizar documento localmente
      setDocumentos(prev => prev.map(doc => 
        doc.id_tramitacao === tramitacaoCiencia.id_tramitacao 
          ? { ...doc, teve_ciencia: true, assinado_digitalmente: true, data_ciencia: new Date() }
          : doc
      ));

      // Fechar modal de ciência e abrir documento
      setShowModalCiencia(false);
      const docParaAbrir = { ...tramitacaoCiencia, teve_ciencia: true };
      setTramitacaoCiencia(null);
      setCertificadoCiencia(null);
      
      // Agora pode abrir o documento
      await abrirDocumento(docParaAbrir);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao dar ciência do documento');
    } finally {
      setSalvandoCiencia(false);
    }
  };

  const encaminharProcesso = async () => {
    if (!selectedDocumento || !destinoSelecionado) {
      setError('Selecione um destino para encaminhar');
      return;
    }

    try {
      setSalvandoEncaminhar(true);
      setError('');

      // Buscar certificado se necessário
      let idCertificado = null;
      if (usarCertificacaoDevolutiva) {
        try {
          const certResponse = await api.get('/certificados/meu-certificado');
          if (certResponse.data.success && certResponse.data.data && certResponse.data.data.certificado) {
            const certificado = certResponse.data.data.certificado;
            // Verificar se o certificado é válido
            if (certResponse.data.data.valido && certificado.status === 'ativo') {
              idCertificado = certificado.id_certificado;
            } else {
              setError('Seu certificado digital está expirado ou inválido. Acesse "Meu Certificado" para emitir um novo.');
              setSalvandoEncaminhar(false);
              return;
            }
          } else {
            setError('É necessário ter um certificado digital válido para usar certificação. Acesse "Meu Certificado" para emitir um.');
            setSalvandoEncaminhar(false);
            return;
          }
        } catch (certError) {
          if (certError.response?.status === 404) {
            setError('É necessário ter um certificado digital válido para usar certificação. Acesse "Meu Certificado" para emitir um.');
          } else {
            setError('Erro ao buscar certificado. Certifique-se de ter um certificado válido.');
          }
          setSalvandoEncaminhar(false);
          return;
        }
      }

      await api.post(`/protocolos/${selectedDocumento.id_protocolo}/encaminhar`, {
        destino_tipo: destinoSelecionado.tipo,
        destino_id: destinoSelecionado.id,
        observacoes: observacoesEncaminhar,
        usar_certificacao: usarCertificacaoDevolutiva,
        id_certificado: idCertificado
      });

      // Recarregar documentos
      if (abaAtiva === 'recebidos') {
        await carregarDocumentosRecebidos();
      } else {
        await carregarDocumentosEnviados();
      }

      // Fechar modais
      setShowModalEncaminhar(false);
      setShowModal(false);
      setDestinoSelecionado(null);
      setObservacoesEncaminhar('');
      setUsarCertificacaoDevolutiva(false);
      
      // Mostrar sucesso
      setError('');
      alert('Processo encaminhado com sucesso!');
      
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao encaminhar processo');
    } finally {
      setSalvandoEncaminhar(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setArquivosSelecionados(prev => [...prev, ...files]);
  };

  const removerArquivo = (index) => {
    setArquivosSelecionados(prev => prev.filter((_, i) => i !== index));
  };

  const adicionarArquivos = async () => {
    if (!selectedDocumento || arquivosSelecionados.length === 0) {
      setError('Selecione pelo menos um arquivo para adicionar');
      return;
    }

    try {
      setEnviandoArquivos(true);
      setError('');

      const formData = new FormData();
      arquivosSelecionados.forEach((file) => {
        formData.append('anexos', file);
      });

      await api.post(`/protocolos/${selectedDocumento.id_protocolo}/anexos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Recarregar documento para ver os novos anexos
      if (selectedDocumento.id_protocolo) {
        try {
          const response = await api.get(`/protocolos/${selectedDocumento.id_protocolo}`);
          if (response.data.success) {
            setSelectedDocumento(response.data.data);
          }
        } catch (err) {
          // Ignorar erro ao recarregar
        }
      }

      // Fechar modal e limpar
      setShowModalUpload(false);
      setArquivosSelecionados([]);
      
      // Mostrar sucesso
      setError('');
      alert(`${arquivosSelecionados.length} arquivo(s) adicionado(s) com sucesso!`);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao adicionar arquivos');
    } finally {
      setEnviandoArquivos(false);
    }
  };

  const formatarTamanhoArquivo = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Verificar se usuário é gestor (nivel >= 2)
  const isGestor = user?.nivel_permissao >= 2 || user?.tipo_usuario === 'admin';

  // Carregar versões de um anexo
  const carregarVersoes = async (idProtocolo, idAnexo) => {
    try {
      const response = await api.get(`/versoes/${idProtocolo}/${idAnexo}`);
      if (response.data.success) {
        setVersoesAnexo(response.data.data || []);
        setAnexoSelecionadoVersoes(idAnexo);
        setMostrarVersoes(true);
      }
    } catch (err) {
      setError('Erro ao carregar versões do documento');
    }
  };

  // Carregar workflow do protocolo
  const carregarWorkflow = async (idProtocolo) => {
    try {
      setWorkflowInstancia(null); // Limpar antes de buscar
      
      // Buscar instância de workflow (todos os status)
      const response = await api.get(`/workflow/meus-workflows`);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Workflows retornados:', {
          success: response.data?.success,
          total: response.data?.data?.length || 0,
          protocoloId: idProtocolo
        });
      }
      
      if (response.data.success && response.data.data) {
        // Buscar workflow do protocolo em qualquer status
        const workflow = response.data.data.find(w => {
          const protocoloId = w.protocolo?.id_protocolo || w.id_protocolo;
          return protocoloId === parseInt(idProtocolo);
        });
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Workflow encontrado:', {
            encontrado: !!workflow,
            workflow: workflow,
            id_instancia: workflow?.id_instancia
          });
        }
        
        if (workflow && workflow.id_instancia) {
          const detalhes = await api.get(`/workflow/${workflow.id_instancia}`);
          if (detalhes.data.success && detalhes.data.data) {
            setWorkflowInstancia(detalhes.data.data);
            if (process.env.NODE_ENV === 'development') {
              console.log('Workflow carregado com sucesso:', detalhes.data.data);
            }
            return;
          }
        }
      }
      
      // Se não encontrou, limpar estado
      setWorkflowInstancia(null);
      if (process.env.NODE_ENV === 'development') {
        console.log('Workflow não encontrado para protocolo:', idProtocolo);
      }
    } catch (err) {
      // Workflow não encontrado ou erro - limpar estado
      setWorkflowInstancia(null);
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro ao buscar workflow:', err);
      }
    }
  };

  // Carregar workflow quando abrir documento
  useEffect(() => {
    if (selectedDocumento && selectedDocumento.id_protocolo) {
      carregarWorkflow(selectedDocumento.id_protocolo);
    }
  }, [selectedDocumento]);

  const fecharModal = async () => {
    setDevolutiva('');
    setUsarCertificacaoDevolutiva(false);
    // Registrar fechamento do protocolo se estava aberto
    if (selectedDocumento && selectedDocumento.id_protocolo) {
      try {
        await api.post(`/protocolos/${selectedDocumento.id_protocolo}/fechar`, {
          id_tramitacao: selectedDocumento.id_tramitacao || null
        });
      } catch (fecharError) {
        // Log apenas em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          console.warn('Erro ao registrar fechamento (continuando):', fecharError);
        }
        // Continuar mesmo se falhar
      }
    }
    
    // Limpar visualização de anexo
    if (urlVisualizacao) {
      window.URL.revokeObjectURL(urlVisualizacao);
      setUrlVisualizacao(null);
    }
    setAnexoVisualizando(null);
    
    setShowModal(false);
    setSelectedDocumento(null);
    setHistorico([]);
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      'memorando': 'Memorando',
      'oficio': 'Ofício',
      'oficio_circular': 'Ofício Circular',
      'requerimento': 'Requerimento',
      'outro': 'Outro'
    };
    return tipos[tipo] || tipo;
  };

  const getPrioridadeClass = (prioridade) => {
    const classes = {
      'baixa': 'prioridade-baixa',
      'normal': 'prioridade-normal',
      'alta': 'prioridade-alta',
      'urgente': 'prioridade-urgente'
    };
    return classes[prioridade] || '';
  };

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const documentosNaoLidos = documentos && Array.isArray(documentos) ? documentos.filter(doc => doc && !doc.lido).length : 0;

  // Debug: Log do estado antes de renderizar
  if (process.env.NODE_ENV === 'development') {
    console.log('🎨 Renderizando Protocolos:', {
      abaAtiva,
      loading,
      documentosCount: documentos && Array.isArray(documentos) ? documentos.length : 0,
      error,
      pathname: location?.pathname || 'N/A'
    });
  }

  return (
    <div className="documentos-inbox" style={{ minHeight: '400px', position: 'relative', zIndex: 1 }}>
      {/* Debug: Sempre mostrar informações de debug em desenvolvimento */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ 
          position: 'fixed', 
          top: '10px', 
          right: '10px', 
          background: 'rgba(0,0,0,0.8)', 
          color: 'white', 
          padding: '10px', 
          borderRadius: 0,
          fontSize: '12px',
          zIndex: 9999,
          maxWidth: '300px'
        }}>
          <div>🔍 Debug:</div>
          <div>Aba: {abaAtiva}</div>
          <div>Loading: {loading ? 'Sim' : 'Não'}</div>
          <div>Docs: {documentos && Array.isArray(documentos) ? documentos.length : 0}</div>
          <div>Rota: {location?.pathname || 'N/A'}</div>
          {error && <div style={{color: 'red'}}>Erro: {error}</div>}
        </div>
      )}
      
      {loading && (!documentos || documentos.length === 0) && (
        <div className="documentos-loading">
          <div className="loading-spinner"></div>
          <p>Carregando documentos...</p>
        </div>
      )}
      {error && (
        <div className="inbox-error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button 
            onClick={() => setError('')} 
            style={{ 
              marginLeft: 'auto', 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer',
              fontSize: '1.2rem',
              color: '#DC2626'
            }}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Abas - SEMPRE VISÍVEIS */}
      <div className="documentos-abas" style={{ display: 'flex', visibility: 'visible', opacity: 1 }}>
        <button
          className={`aba-item ${abaAtiva === 'recebidos' ? 'ativa' : ''}`}
          onClick={() => {
            setAbaAtiva('recebidos');
            // Navegar para a rota de recebidos se não estiver nela
            if (!location?.pathname?.includes('/documentos') || location?.pathname?.includes('/enviados')) {
              navigate('/documentos', { replace: true });
            }
          }}
        >
          📥 Recebidos
        </button>
        <button
          className={`aba-item ${abaAtiva === 'enviados' ? 'ativa' : ''}`}
          onClick={() => {
            setAbaAtiva('enviados');
            // Navegar para a rota de enviados se não estiver nela
            if (!location?.pathname?.includes('/enviados')) {
              navigate('/documentos/enviados', { replace: true });
            }
          }}
        >
          📤 Enviados
        </button>
      </div>

      {/* Header - SEMPRE VISÍVEL */}
      <div className="inbox-header" style={{ display: 'flex', visibility: 'visible', opacity: 1 }}>
        <div className="header-title-section">
          <h1 style={{ margin: 0, padding: 0 }}>{abaAtiva === 'recebidos' ? 'Documentos Recebidos' : 'Documentos Enviados'}</h1>
          <p className="header-subtitle" style={{ margin: '0.5rem 0 0 0' }}>
            {abaAtiva === 'recebidos' 
              ? 'Caixa de entrada de documentos e processos' 
              : 'Documentos criados e enviados pela sua secretaria'}
          </p>
        </div>
        {abaAtiva === 'recebidos' && (
          <div className="inbox-stats">
            <div className="stat-item">
              <span className="stat-number">{documentosNaoLidos}</span>
              <span className="stat-label">Não lidos</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{documentos && Array.isArray(documentos) ? documentos.length : 0}</span>
              <span className="stat-label">Total</span>
            </div>
          </div>
        )}
        {abaAtiva === 'enviados' && (
          <div className="inbox-stats">
            <div className="stat-item">
              <span className="stat-number">{documentos && Array.isArray(documentos) ? documentos.length : 0}</span>
              <span className="stat-label">Total Enviados</span>
            </div>
          </div>
        )}
      </div>

      {/* Container - SEMPRE VISÍVEL, mostra loading ou conteúdo */}
      <div className="inbox-container" style={{ 
        display: loading ? 'none' : 'block', 
        visibility: loading ? 'hidden' : 'visible',
        opacity: loading ? 0 : 1,
        minHeight: '200px'
      }}>
        {!documentos || documentos.length === 0 ? (
          <div className="inbox-empty" style={{ padding: '3rem', textAlign: 'center' }}>
            <div className="empty-icon" style={{ fontSize: '4rem', marginBottom: '1rem' }}>{abaAtiva === 'recebidos' ? '📬' : '📤'}</div>
            <h3 style={{ margin: '0.5rem 0', color: '#1F2937' }}>{abaAtiva === 'recebidos' ? 'Nenhum documento recebido' : 'Nenhum documento enviado'}</h3>
            <p style={{ margin: '0.5rem 0', color: '#6B7280' }}>{abaAtiva === 'recebidos' ? 'Sua caixa de entrada está vazia' : 'Você ainda não enviou nenhum documento'}</p>
          </div>
        ) : (
            <div className={`documentos-list ${abaAtiva === 'recebidos' ? 'listview-recebidos' : 'cardview-enviados'}`}>
              {documentos.map((documento) => (
                abaAtiva === 'recebidos' ? (
                  // ListView para Recebidos
                  <div
                    key={documento.id_tramitacao}
                    className={`documento-list-item ${!documento.lido ? 'nao-lido' : ''} ${documento.fechado ? 'fechado' : ''}`}
                    onClick={() => abrirDocumento(documento)}
                  >
                    {!documento.lido && <div className="indicador-nao-lido"></div>}
                    <div className="list-item-icons">
                      {/* Ícone de recebido - sempre mostrar se foi recebido */}
                      {documento.status_tramitacao === 'recebido' || documento.data_recebimento ? (
                        <span className="icon-recebido" title="Documento recebido">📥</span>
                      ) : null}
                      {/* Ícone de ciência - mostrar se teve ciência */}
                      {documento.teve_ciencia ? (
                        <span className="icon-ciencia" title="Ciência dada com assinatura digital">✅</span>
                      ) : null}
                      {/* Ícone de fechado/aberto - sempre mostrar para recebidos */}
                      {documento.aberto ? (
                        <span className="icon-aberto" title="Documento aberto">🔓</span>
                      ) : (
                        <span className="icon-fechado" title="Documento fechado (não aberto)">🔒</span>
                      )}
                      {/* Ícone de certificação - ANTES da prioridade */}
                      {(documento.usar_certificacao || documento.usar_certificacao_protocolo || documento.usar_certificacao_tramitacao) && (
                        <span className="icon-certificacao" title="Assinado digitalmente com certificado">✍️</span>
                      )}
                      {/* Ícone de prioridade */}
                      {documento.prioridade === 'urgente' && (
                        <span className="icon-prioridade-urgente" title="Prioridade Urgente">⚡</span>
                      )}
                      {documento.prioridade === 'alta' && (
                        <span className="icon-prioridade-alta" title="Prioridade Alta">🔴</span>
                      )}
                      {documento.prioridade === 'normal' && (
                        <span className="icon-prioridade-normal" title="Prioridade Normal">🟡</span>
                      )}
                      {documento.prioridade === 'baixa' && (
                        <span className="icon-prioridade-baixa" title="Prioridade Baixa">🟢</span>
                      )}
                    </div>
                    <div className="list-item-tags">
                      <span className="tipo-badge">{getTipoLabel(documento.tipo_documento).toUpperCase()}</span>
                      <span className={`prioridade-badge ${getPrioridadeClass(documento.prioridade)}`}>
                        {documento.prioridade.toUpperCase()}
                      </span>
                    </div>
                    <div className="list-item-meta">
                      <span className="list-item-origem">
                        De: {documento.usuario_origem?.secretaria?.sigla || 
                             documento.usuario_origem?.secretaria?.descricao || 
                             documento.setor_origem?.sigla || 
                             documento.setor_origem?.nome_setor || 
                             documento.protocolo?.usuarioOrigem?.secretaria?.sigla ||
                             documento.protocolo?.usuarioOrigem?.secretaria?.descricao ||
                             documento.protocolo?.setorOrigem?.sigla || 
                             documento.protocolo?.setorOrigem?.nome_setor || 
                             'N/A'}
                      </span>
                      <span className="list-item-separator">•</span>
                      <span className="list-item-data">{formatarData(documento.data_envio)}</span>
                    </div>
                    <div className="list-item-info">
                      <div className="list-item-numero">{documento.numero_protocolo}</div>
                      <div className="list-item-assunto">{documento.assunto}</div>
                    </div>
                    <button 
                      className="btn-abrir-list"
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirDocumento(documento);
                      }}
                    >
                      Abrir
                    </button>
                  </div>
                ) : (
                  // ListView para Enviados (mesmo formato)
                  <div
                    key={documento.id_protocolo}
                    className="documento-list-item"
                    onClick={() => abrirDocumento(documento)}
                  >
                    <div className="list-item-icons">
                      {/* Verificar status das tramitações para mostrar ícones - ANTES do tipo */}
                      {(() => {
                        const tramitacoes = documento.tramitacoes || [];
                        // Ordenar tramitações por data_envio DESC para pegar a mais recente
                        const tramitacoesOrdenadas = [...tramitacoes].sort((a, b) => {
                          const dataA = a.data_envio ? new Date(a.data_envio) : new Date(0);
                          const dataB = b.data_envio ? new Date(b.data_envio) : new Date(0);
                          return dataB - dataA;
                        });
                        const ultimaTramitacao = tramitacoesOrdenadas.length > 0 ? tramitacoesOrdenadas[0] : null;
                        
                        // Debug em desenvolvimento
                        if (process.env.NODE_ENV === 'development' && ultimaTramitacao) {
                          console.log('Tramitação para ícone:', {
                            status: ultimaTramitacao.status,
                            data_recebimento: ultimaTramitacao.data_recebimento,
                            teve_ciencia: ultimaTramitacao.teve_ciencia,
                            id_tramitacao: ultimaTramitacao.id_tramitacao
                          });
                        }
                        
                        const foiRecebido = ultimaTramitacao && (ultimaTramitacao.status === 'recebido' || ultimaTramitacao.data_recebimento);
                        const foiLido = ultimaTramitacao && (ultimaTramitacao.status === 'recebido' || ultimaTramitacao.data_recebimento);
                        const teveCiencia = ultimaTramitacao && ultimaTramitacao.teve_ciencia;
                        const foiEnviado = tramitacoes.length > 0 && (!foiRecebido || ultimaTramitacao.status === 'enviado');
                        
                        // Prioridade: ciência > lido > recebido > enviado
                        if (teveCiencia) {
                          return <span className="icon-ciencia" title="Ciência dada com assinatura digital">✅</span>;
                        } else if (foiLido) {
                          return <span className="icon-lido" title="Documento lido pelo destinatário">👁️</span>;
                        } else if (foiRecebido) {
                          return <span className="icon-recebido" title="Documento recebido pelo destinatário">📥</span>;
                        } else if (foiEnviado) {
                          return <span className="icon-enviado" title="Documento enviado">📤</span>;
                        }
                        return null;
                      })()}
                      {/* Ícone de certificação */}
                      {(documento.usar_certificacao || documento.protocolo?.usar_certificacao) && (
                        <span className="icon-certificacao" title="Assinado digitalmente com certificado">✍️</span>
                      )}
                      {/* Ícone de prioridade */}
                      {documento.prioridade === 'urgente' && (
                        <span className="icon-prioridade-urgente" title="Prioridade Urgente">⚡</span>
                      )}
                      {documento.prioridade === 'alta' && (
                        <span className="icon-prioridade-alta" title="Prioridade Alta">🔴</span>
                      )}
                      {documento.prioridade === 'normal' && (
                        <span className="icon-prioridade-normal" title="Prioridade Normal">🟡</span>
                      )}
                      {documento.prioridade === 'baixa' && (
                        <span className="icon-prioridade-baixa" title="Prioridade Baixa">🟢</span>
                      )}
                    </div>
                    <div className="list-item-tags">
                      <span className="tipo-badge">{getTipoLabel(documento.tipo_documento).toUpperCase()}</span>
                      <span className={`prioridade-badge ${getPrioridadeClass(documento.prioridade)}`}>
                        {documento.prioridade.toUpperCase()}
                      </span>
                    </div>
                    <div className="list-item-meta">
                      <span className="list-item-destino">Para: {documento.setor_destino?.sigla || documento.setor_destino?.nome_setor || 'N/A'}</span>
                      <span className="list-item-separator">•</span>
                      <span className="list-item-data">{formatarData(documento.data_protocolo || documento.data_documento)}</span>
                    </div>
                    <div className="list-item-info">
                      <div className="list-item-numero">{documento.numero_protocolo}</div>
                      <div className="list-item-assunto">{documento.assunto}</div>
                    </div>
                    <button 
                      className="btn-abrir-list"
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirDocumento(documento);
                      }}
                    >
                      Abrir
                    </button>
                  </div>
                )
              ))}
            </div>
        )}
      </div>

      {/* Modal de Detalhes */}
      {showModal && selectedDocumento && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className={`modal-content-documento ${anexoVisualizando ? 'com-visualizador' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-documento">
              <div className="modal-header-content">
                <div className="modal-header-tags">
                  <span className="tipo-badge">{getTipoLabel(selectedDocumento.tipo_documento).toUpperCase()}</span>
                  <span className={`prioridade-badge ${getPrioridadeClass(selectedDocumento.prioridade)}`}>
                    {selectedDocumento.prioridade.toUpperCase()}
                  </span>
                </div>
                <div className="modal-header-info">
                  <div className="modal-numero">{selectedDocumento.numero_protocolo}</div>
                  <div className="modal-assunto">{selectedDocumento.assunto}</div>
                </div>
              </div>
              <button className="modal-close" onClick={fecharModal}>×</button>
            </div>

            <div className={`modal-body-documento ${anexoVisualizando ? 'com-visualizador' : ''}`}>
              <div className="documento-info">
                <div className="info-section">
                  <h3>Informações do Documento</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Origem:</label>
                      <span>
                        {selectedDocumento.usuarioOrigem?.secretaria?.sigla || 
                         selectedDocumento.usuarioOrigem?.secretaria?.descricao || 
                         selectedDocumento.setorOrigem?.sigla || 
                         selectedDocumento.setorOrigem?.nome_setor || 
                         selectedDocumento.setor_origem?.sigla ||
                         selectedDocumento.setor_origem?.nome_setor ||
                         '-'}
                      </span>
                    </div>
                    <div className="info-item">
                      <label>Destino:</label>
                      <span>{selectedDocumento.setorDestino?.nome_setor || '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>Data do Documento:</label>
                      <span>{formatarData(selectedDocumento.data_documento)}</span>
                    </div>
                    <div className="info-item">
                      <label>Data de Protocolo:</label>
                      <span>{formatarData(selectedDocumento.data_protocolo)}</span>
                    </div>
                    <div className="info-item">
                      <label>Remetente:</label>
                      <span>{selectedDocumento.usuarioOrigem?.nome_completo || '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>Status:</label>
                      <span className={`status-badge status-${selectedDocumento.status}`}>
                        {selectedDocumento.status}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedDocumento.conteudo && (
                  <div className="info-section">
                    <h3>Conteúdo</h3>
                    <div className="conteudo-texto">
                      {selectedDocumento.conteudo}
                    </div>
                  </div>
                )}

                {selectedDocumento.anexos && Array.isArray(selectedDocumento.anexos) && selectedDocumento.anexos.length > 0 && (
                  <div className="info-section">
                    <h3>Anexos ({selectedDocumento.anexos.length})</h3>
                    <div className="anexos-list-modal">
                      {selectedDocumento.anexos.map((anexo) => (
                        <div key={anexo.id_anexo} className="anexo-item-modal">
                          <span className="anexo-icon">📎</span>
                          <span className="anexo-nome">{anexo.nome_original}</span>
                          <span className="anexo-tamanho">
                            {anexo.tamanho ? (anexo.tamanho / 1024 / 1024).toFixed(2) + ' MB' : ''}
                          </span>
                          <div className="anexo-buttons">
                            <button
                              className="btn-visualizar-anexo"
                              onClick={async (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                try {
                                  setAnexoVisualizando(anexo);
                                  setError('');
                                  
                                  // Log para debug
                                  if (process.env.NODE_ENV === 'development') {
                                    console.log('Iniciando visualização do anexo:', anexo);
                                  }
                                  
                                  // Buscar o arquivo para visualização
                                  const response = await api.get(`/protocolos/anexos/${anexo.id_anexo}/download`, {
                                    responseType: 'blob',
                                    headers: {
                                      'Accept': 'application/pdf,application/octet-stream,*/*'
                                    }
                                  });
                                  
                                  // Log para debug
                                  if (process.env.NODE_ENV === 'development') {
                                    console.log('Resposta recebida:', {
                                      size: response.data.size,
                                      type: response.data.type,
                                      headers: response.headers
                                    });
                                  }
                                  
                                  // Determinar tipo MIME
                                  let mimeType = 'application/pdf';
                                  if (anexo.tipo_mime) {
                                    mimeType = anexo.tipo_mime;
                                  } else if (anexo.nome_original.toLowerCase().endsWith('.pdf')) {
                                    mimeType = 'application/pdf';
                                  } else if (response.headers && response.headers['content-type']) {
                                    mimeType = response.headers['content-type'].split(';')[0];
                                  } else if (response.data && response.data.type) {
                                    mimeType = response.data.type;
                                  }
                                  
                                  // Log para debug
                                  if (process.env.NODE_ENV === 'development') {
                                    console.log('Tipo MIME determinado:', mimeType);
                                  }
                                  
                                  // Verificar se o blob tem dados
                                  if (!response.data || response.data.size === 0) {
                                    throw new Error('Arquivo vazio ou inválido');
                                  }
                                  
                                  // Criar blob com tipo MIME correto
                                  const blob = new Blob([response.data], { type: mimeType });
                                  const url = window.URL.createObjectURL(blob);
                                  
                                  // Log para debug
                                  if (process.env.NODE_ENV === 'development') {
                                    console.log('Blob criado:', {
                                      size: blob.size,
                                      type: blob.type,
                                      url: url
                                    });
                                  }
                                  
                                  setUrlVisualizacao(url);
                                } catch (err) {
                                  // Log de erro
                                  if (process.env.NODE_ENV === 'development') {
                                    console.error('Erro ao visualizar anexo:', err);
                                    console.error('Detalhes do erro:', {
                                      message: err.message,
                                      response: err.response,
                                      stack: err.stack
                                    });
                                  }
                                  setError(`Erro ao visualizar anexo: ${err.message || 'Erro desconhecido'}`);
                                  setAnexoVisualizando(null);
                                  setUrlVisualizacao(null);
                                }
                              }}
                            >
                              👁️ Visualizar
                            </button>
                            <a
                              href={`${process.env.REACT_APP_API_URL || ''}/api/protocolos/anexos/${anexo.id_anexo}/download`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-download-anexo"
                              onClick={async (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                try {
                                  // Fazer download via API com autenticação
                                  const response = await api.get(`/protocolos/anexos/${anexo.id_anexo}/download`, {
                                    responseType: 'blob'
                                  });
                                  const url = window.URL.createObjectURL(new Blob([response.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', anexo.nome_original);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  window.URL.revokeObjectURL(url);
                                } catch (err) {
                                  // Log apenas em desenvolvimento
                                  if (process.env.NODE_ENV === 'development') {
                                    console.error('Erro ao baixar anexo:', err);
                                  }
                                  setError('Erro ao baixar anexo');
                                }
                              }}
                            >
                              📥 Baixar
                            </a>
                            {isGestor && (
                              <button
                                className="btn-versoes-anexo"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  await carregarVersoes(selectedDocumento.id_protocolo, anexo.id_anexo);
                                }}
                                title="Ver versões deste documento"
                              >
                                📚 Versões
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Seção de Versões */}
                {mostrarVersoes && versoesAnexo.length > 0 && (
                  <div className="info-section versoes-section">
                    <h3>Versões do Documento</h3>
                    <div className="versoes-list">
                      {versoesAnexo.map((versao) => (
                        <div key={versao.id_versao} className={`versao-item ${versao.versao_atual ? 'versao-atual' : ''}`}>
                          <div className="versao-header">
                            <span className="versao-numero">Versão {versao.numero_versao}</span>
                            {versao.versao_atual && <span className="versao-badge-atual">Atual</span>}
                            <span className="versao-data">
                              {new Date(versao.data_upload).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <div className="versao-info">
                            <div className="versao-nome">{versao.nome_original}</div>
                            {versao.descricao_versao && (
                              <div className="versao-descricao">{versao.descricao_versao}</div>
                            )}
                            {versao.motivo_alteracao && (
                              <div className="versao-motivo">
                                <strong>Motivo:</strong> {versao.motivo_alteracao}
                              </div>
                            )}
                            <div className="versao-usuario">
                              Por: {versao.usuarioUpload?.nome_completo || 'N/A'}
                            </div>
                          </div>
                          <div className="versao-actions">
                            <a
                              href="#"
                              className="btn-download-versao"
                              onClick={async (e) => {
                                e.preventDefault();
                                try {
                                  const response = await api.get(`/versoes/${versao.id_versao}/download`, {
                                    responseType: 'blob'
                                  });
                                  const url = window.URL.createObjectURL(new Blob([response.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', versao.nome_original);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  window.URL.revokeObjectURL(url);
                                } catch (err) {
                                  setError('Erro ao baixar versão');
                                }
                              }}
                            >
                              📥 Baixar
                            </a>
                            {!versao.versao_atual && isGestor && (
                              <button
                                className="btn-restaurar-versao"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  if (window.confirm(`Deseja restaurar a versão ${versao.numero_versao}?`)) {
                                    try {
                                      await api.post(`/versoes/${versao.id_versao}/restaurar`);
                                      setError('');
                                      alert('Versão restaurada com sucesso!');
                                      await carregarVersoes(selectedDocumento.id_protocolo, anexoSelecionadoVersoes);
                                    } catch (err) {
                                      setError(err.response?.data?.message || 'Erro ao restaurar versão');
                                    }
                                  }
                                }}
                              >
                                🔄 Restaurar
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      className="btn-fechar-versoes"
                      onClick={() => {
                        setMostrarVersoes(false);
                        setVersoesAnexo([]);
                        setAnexoSelecionadoVersoes(null);
                      }}
                    >
                      Fechar Versões
                    </button>
                  </div>
                )}

                {/* Seção de Workflow */}
                {workflowInstancia && (
                  <div className="info-section workflow-section">
                    <h3>Workflow do Processo</h3>
                    <div className="workflow-info">
                      <div className="workflow-status">
                        <span className={`workflow-status-badge status-${workflowInstancia.status}`}>
                          {workflowInstancia.status === 'em_andamento' && '🔄 Em Andamento'}
                          {workflowInstancia.status === 'aprovado' && '✅ Aprovado'}
                          {workflowInstancia.status === 'rejeitado' && '❌ Rejeitado'}
                          {workflowInstancia.status === 'finalizado' && '✔️ Finalizado'}
                          {workflowInstancia.status === 'cancelado' && '🚫 Cancelado'}
                        </span>
                      </div>
                      {workflowInstancia.etapaAtual && (
                        <div className="workflow-etapa-atual">
                          <strong>Etapa Atual:</strong> {workflowInstancia.etapaAtual.nome_etapa}
                          {workflowInstancia.etapaAtual.descricao && (
                            <div className="workflow-etapa-desc">{workflowInstancia.etapaAtual.descricao}</div>
                          )}
                        </div>
                      )}
                      {workflowInstancia.template && (
                        <div className="workflow-template">
                          <strong>Template:</strong> {workflowInstancia.template.nome}
                        </div>
                      )}
                      {workflowInstancia.execucoes && workflowInstancia.execucoes.length > 0 && (
                        <div className="workflow-historico">
                          <strong>Histórico de Execução:</strong>
                          <div className="workflow-execucoes-list">
                            {workflowInstancia.execucoes.map((exec, idx) => (
                              <div key={idx} className="workflow-execucao-item">
                                <span className="exec-acao">{exec.acao}</span>
                                <span className="exec-usuario">{exec.usuarioExecutor?.nome_completo || 'N/A'}</span>
                                <span className="exec-data">
                                  {new Date(exec.data_execucao).toLocaleDateString('pt-BR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                                {exec.observacoes && (
                                  <div className="exec-observacoes">{exec.observacoes}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="info-section">
                  <h3>Histórico de Tramitação</h3>
                  <div className="historico-timeline">
                    {!historico || historico.length === 0 ? (
                      <div className="historico-empty">
                        <p>Nenhuma tramitação registrada</p>
                      </div>
                    ) : (
                      historico.map((tramitacao, index) => (
                        <div key={tramitacao.id_tramitacao} className="timeline-item">
                          <div className="timeline-marker"></div>
                          <div className="timeline-content">
                            <div className="timeline-header">
                              <span className="timeline-action">
                                {tramitacao.status === 'enviado' && '📤 Enviado'}
                                {tramitacao.status === 'recebido' && '📥 Recebido'}
                                {tramitacao.status === 'em_analise' && '🔍 Em Análise'}
                                {tramitacao.status === 'respondido' && '✅ Respondido'}
                                {tramitacao.status === 'arquivado' && '📁 Arquivado'}
          </span>
                              <span className="timeline-date">{formatarData(tramitacao.data_envio)}</span>
                            </div>
                            <div className="timeline-details">
                              <p>
                                <strong>De:</strong> {tramitacao.setorOrigem?.nome_setor || '-'}
                                {' → '}
                                <strong>Para:</strong> {tramitacao.setorDestino?.nome_setor || '-'}
                              </p>
                              {tramitacao.observacoes && (
                                <p className="timeline-observacoes">
                                  <strong>Observações:</strong> {tramitacao.observacoes}
                                </p>
                              )}
                              {tramitacao.resposta && (
                                <p className="timeline-resposta">
                                  <strong>Resposta:</strong> {tramitacao.resposta}
                                </p>
                              )}
                              {tramitacao.data_recebimento && (
                                <p className="timeline-recebimento">
                                  <strong>Recebido em:</strong> {formatarData(tramitacao.data_recebimento)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Campo de Feedback/Devolutiva - para recebidos (usuário destino, setor destino, admin ou gestor) */}
                {abaAtiva === 'recebidos' && selectedDocumento && selectedDocumento.id_tramitacao && (
                  <div className="info-section feedback-section">
                    <div className="feedback-header">
                      <div className="feedback-header-content">
                        <div className="feedback-title-wrapper">
                          <div className="feedback-icon-large">💬</div>
                          <div>
                            <h3>Feedback / Devolutiva</h3>
                            <p className="feedback-subtitle">Registre sua resposta oficial sobre este documento</p>
                          </div>
                        </div>
                      </div>
                      <div className="feedback-info-tips">
                        <div className="tip-item">
                          <span className="tip-icon">💡</span>
                          <span>Seja claro e objetivo na sua resposta</span>
                        </div>
                        <div className="tip-item">
                          <span className="tip-icon">🔒</span>
                          <span>Use certificação para maior segurança</span>
                        </div>
                      </div>
                    </div>
                    
                    {selectedDocumento.resposta ? (
                      <div className="feedback-existente">
                        <div className="feedback-status-card">
                          <div className="feedback-status-header">
                            <div className="feedback-status-badge success">
                              <span className="feedback-status-icon">✓</span>
                              <span>Feedback Registrado</span>
                            </div>
                            {selectedDocumento.usar_certificacao && (
                              <div className="feedback-cert-badge">
                                <span className="cert-icon">✍️</span>
                                <span>Certificado Digital</span>
                              </div>
                            )}
                          </div>
                          <div className="feedback-content-card">
                            <div className="feedback-text-display">
                              {selectedDocumento.resposta}
                            </div>
                            <div className="feedback-timestamp">
                              Registrado em: {formatarData(new Date())}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="feedback-form">
                        <div className="feedback-input-container">
                          <div className="feedback-label-group">
                            <label htmlFor="feedback-textarea" className="feedback-label">
                              <span className="label-icon">✍️</span>
                              Sua Resposta ou Feedback
                            </label>
                            <span className="label-required">* Obrigatório</span>
                          </div>
                          <div className="textarea-wrapper">
                            <textarea
                              id="feedback-textarea"
                              className="feedback-textarea"
                              placeholder="Exemplo: Recebido e analisado. Aprovado conforme solicitação. Encaminhado para setor responsável para as devidas providências..."
                              value={devolutiva}
                              onChange={(e) => setDevolutiva(e.target.value)}
                              rows={6}
                              maxLength={5000}
                            />
                            <div className="textarea-footer">
                              <div className="char-count-wrapper">
                                <span className={`char-count ${devolutiva.length > 4500 ? 'warning' : ''} ${devolutiva.length === 5000 ? 'error' : ''}`}>
                                  {devolutiva.length.toLocaleString()} / 5.000 caracteres
                                </span>
                                {devolutiva.length < 10 && devolutiva.length > 0 && (
                                  <span className="char-min-hint">Mínimo: 10 caracteres</span>
                                )}
                              </div>
                              <div className="textarea-actions-mini">
                                <button
                                  type="button"
                                  className="btn-clear-mini"
                                  onClick={() => setDevolutiva('')}
                                  title="Limpar campo"
                                  disabled={!devolutiva.trim()}
                                >
                                  🗑️ Limpar
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="feedback-options-card">
                          <div className="options-header">
                            <span className="options-title">⚙️ Opções de Envio</span>
                          </div>
                          <div 
                            className="feedback-checkbox-card"
                            onClick={() => setUsarCertificacaoDevolutiva(!usarCertificacaoDevolutiva)}
                          >
                            <div className="checkbox-wrapper">
                              <input
                                type="checkbox"
                                checked={usarCertificacaoDevolutiva}
                                onChange={(e) => setUsarCertificacaoDevolutiva(e.target.checked)}
                                className="checkbox-input"
                                id="checkbox-certificacao"
                              />
                              <div className={`checkbox-custom ${usarCertificacaoDevolutiva ? 'checked' : ''}`}>
                                {usarCertificacaoDevolutiva && <span className="checkbox-check">✓</span>}
                              </div>
                            </div>
                            <div className="checkbox-content">
                              <div className="checkbox-main-text">
                                <span className="checkbox-icon">✍️</span>
                                <span>Enviar com Certificação Digital</span>
                              </div>
                              <div className="checkbox-description">
                                Garante autenticidade, integridade e não-repúdio da sua resposta. Recomendado para documentos oficiais importantes.
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="feedback-actions-bar">
                          <div className="actions-info">
                            {devolutiva.trim().length > 0 && devolutiva.trim().length < 10 && (
                              <div className="validation-message warning">
                                <span>⚠️</span>
                                <span>O feedback deve ter pelo menos 10 caracteres</span>
                              </div>
                            )}
                            {devolutiva.trim().length >= 10 && (
                              <div className="validation-message success">
                                <span>✓</span>
                                <span>Pronto para enviar</span>
                              </div>
                            )}
                          </div>
                          <div className="actions-buttons">
                            <button
                              className="btn-feedback-secondary"
                              onClick={() => {
                                setDevolutiva('');
                                setUsarCertificacaoDevolutiva(false);
                                setError('');
                              }}
                              disabled={salvandoDevolutiva || !devolutiva.trim()}
                              type="button"
                            >
                              <span>↺</span>
                              Cancelar
                            </button>
                            <button
                              className="btn-feedback-primary"
                              onClick={async () => {
                                if (!devolutiva.trim()) {
                                  setError('Por favor, digite um feedback ou resposta');
                                  return;
                                }
                                if (devolutiva.trim().length < 10) {
                                  setError('O feedback deve ter pelo menos 10 caracteres');
                                  return;
                                }
                                try {
                                  setSalvandoDevolutiva(true);
                                  setError('');
                                  const payload = {
                                    resposta: devolutiva.trim(),
                                    usar_certificacao: usarCertificacaoDevolutiva
                                  };
                                  // Se usar certificação, buscar certificado do usuário
                                  if (usarCertificacaoDevolutiva) {
                                    try {
                                      const certResponse = await api.get('/certificados/meu-certificado');
                                      if (certResponse.data.success && certResponse.data.data && certResponse.data.data.certificado) {
                                        const certificado = certResponse.data.data.certificado;
                                        // Verificar se o certificado é válido
                                        if (certResponse.data.data.valido && certificado.status === 'ativo') {
                                          payload.id_certificado = certificado.id_certificado;
                                        } else {
                                          throw new Error('Certificado expirado ou inválido');
                                        }
                                      } else {
                                        throw new Error('Certificado não encontrado');
                                      }
                                    } catch (certError) {
                                      setError('Erro ao buscar certificado. Certifique-se de ter um certificado válido.');
                                      setSalvandoDevolutiva(false);
                                      return;
                                    }
                                  }
                                  await api.put(`/protocolos/recebidos/${selectedDocumento.id_tramitacao}/devolutiva`, payload);
                                  setDevolutiva('');
                                  setUsarCertificacaoDevolutiva(false);
                                  // Recarregar documento
                                  const response = await api.get(`/protocolos/${selectedDocumento.id_protocolo}`);
                                  if (response.data && response.data.success) {
                                    setSelectedDocumento(response.data.data);
                                  }
                                  // Recarregar lista
                                  if (abaAtiva === 'recebidos') {
                                    await carregarDocumentosRecebidos();
                                  }
                                } catch (err) {
                                  setError(err.response?.data?.message || 'Erro ao salvar feedback');
                                } finally {
                                  setSalvandoDevolutiva(false);
                                }
                              }}
                              disabled={salvandoDevolutiva || !devolutiva.trim() || devolutiva.trim().length < 10}
                              type="button"
                            >
                              {salvandoDevolutiva ? (
                                <>
                                  <span className="spinner-small"></span>
                                  <span>Enviando...</span>
                                </>
                              ) : (
                                <>
                                  <span className="btn-icon">📤</span>
                                  <span>Enviar Feedback</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Histórico de Leituras e Acessos - No final do modal */}
              <div className="historico-container">
                {/* Histórico de Leituras */}
                {historicoLeituras && historicoLeituras.length > 0 && (
                  <div className="info-section historico-section">
                    <h3>📖 Histórico de Leituras ({historicoLeituras.length})</h3>
                    <div className="historico-list">
                      {historicoLeituras.map((leitura, index) => (
                        <div key={index} className="historico-item">
                          <div className="historico-icon">👁️</div>
                          <div className="historico-content">
                            <div className="historico-usuario">
                              <strong>{leitura.usuario?.nome_completo || leitura.usuario?.email || 'Usuário desconhecido'}</strong>
                            </div>
                            <div className="historico-acao">
                              Leu o documento
                            </div>
                            <div className="historico-data">
                              {formatarData(leitura.data_leitura)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Histórico de Aberturas/Visitas */}
                {historico && historico.length > 0 && historico.some(item => item.tipo === 'abertura' || item.acao === 'aberto') && (
                  <div className="info-section historico-section">
                    <h3>📋 Histórico de Acessos</h3>
                    <div className="historico-list">
                      {historico
                        .filter(item => item.tipo === 'abertura' || item.acao === 'aberto')
                        .map((item, index) => (
                          <div key={index} className="historico-item">
                            <div className="historico-icon">👁️</div>
                            <div className="historico-content">
                              <div className="historico-usuario">
                                <strong>{item.usuario?.nome_completo || item.usuario?.email || 'Usuário desconhecido'}</strong>
                              </div>
                              <div className="historico-acao">
                                {item.acao === 'aberto' ? 'Visualizou o documento' : item.acao}
                              </div>
                              <div className="historico-data">
                                {formatarData(item.data || item.data_acao)}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer-documento">
              <div className="modal-footer-left">
                {/* Botão Adicionar Arquivos - apenas para gestores */}
                {isGestor && selectedDocumento && (
                  <button 
                    className="btn-primary-outline" 
                    onClick={() => setShowModalUpload(true)}
                    style={{ marginRight: '10px' }}
                  >
                    📎 Adicionar Arquivos
                  </button>
                )}
                {/* Botão Encaminhar - apenas para recebidos */}
                {abaAtiva === 'recebidos' && selectedDocumento && (
                  <button 
                    className="btn-primary-outline" 
                    onClick={() => setShowModalEncaminhar(true)}
                    style={{ marginRight: '10px' }}
                  >
                    ➡️ Encaminhar
                  </button>
                )}
              </div>
              <div className="modal-footer-right">
                <button className="btn-secondary" onClick={fecharModal}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Ciência com Assinatura Digital */}
      {showModalCiencia && tramitacaoCiencia && certificadoCiencia && (
        <div className="modal-overlay" onClick={() => {
          if (!salvandoCiencia) {
            setShowModalCiencia(false);
            setTramitacaoCiencia(null);
            setCertificadoCiencia(null);
          }
        }}>
          <div className="modal-content modal-ciencia" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✍️ Dar Ciência do Documento</h2>
              <button 
                className="btn-fechar-modal"
                onClick={() => {
                  if (!salvandoCiencia) {
                    setShowModalCiencia(false);
                    setTramitacaoCiencia(null);
                    setCertificadoCiencia(null);
                  }
                }}
                disabled={salvandoCiencia}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="ciencia-alert">
                <div className="alert-icon">⚠️</div>
                <div className="alert-content">
                  <h3>Assinatura Digital Obrigatória</h3>
                  <p>Para abrir este documento, é necessário dar ciência com assinatura digital.</p>
                </div>
              </div>
              
              <div className="ciencia-info-card">
                <h4>Informações do Documento</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Protocolo:</label>
                    <span>{tramitacaoCiencia.numero_protocolo || tramitacaoCiencia.id_protocolo}</span>
                  </div>
                  <div className="info-item">
                    <label>Assunto:</label>
                    <span>{tramitacaoCiencia.assunto}</span>
                  </div>
                </div>
              </div>

              <div className="ciencia-certificado-card">
                <h4>Certificado Digital</h4>
                <div className="certificado-info">
                  <p><strong>Número de Série:</strong> {certificadoCiencia.numero_serie}</p>
                  <p><strong>Válido até:</strong> {formatarData(certificadoCiencia.data_expiracao)}</p>
                  <p><strong>Usuário:</strong> {user?.nome_completo}</p>
                </div>
              </div>

              {error && <div className="alert alert-error">{error}</div>}

              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    if (!salvandoCiencia) {
                      setShowModalCiencia(false);
                      setTramitacaoCiencia(null);
                      setCertificadoCiencia(null);
                    }
                  }}
                  disabled={salvandoCiencia}
                >
                  Cancelar
                </button>
                <button
                  className="btn-primary"
                  onClick={darCiencia}
                  disabled={salvandoCiencia}
                >
                  {salvandoCiencia ? (
                    <>
                      <span className="spinner-small"></span>
                      Assinando...
                    </>
                  ) : (
                    <>
                      <span>✍️</span>
                      Dar Ciência e Abrir
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Encaminhamento */}
      {showModalEncaminhar && selectedDocumento && (
        <div className="modal-overlay" onClick={() => {
          if (!salvandoEncaminhar) {
            setShowModalEncaminhar(false);
            setDestinoSelecionado(null);
            setObservacoesEncaminhar('');
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>➡️ Encaminhar Processo</h2>
              <button 
                className="btn-fechar-modal"
                onClick={() => {
                  if (!salvandoEncaminhar) {
                    setShowModalEncaminhar(false);
                    setDestinoSelecionado(null);
                    setObservacoesEncaminhar('');
                  }
                }}
                disabled={salvandoEncaminhar}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="info-section">
                <h3>Informações do Processo</h3>
                <p><strong>Protocolo:</strong> {selectedDocumento.numero_protocolo}</p>
                <p><strong>Assunto:</strong> {selectedDocumento.assunto}</p>
              </div>

              <div className="form-group">
                <label htmlFor="destino-select">Destino (Secretaria/Subsecretaria/Setor) *</label>
                <select
                  id="destino-select"
                  className="form-control"
                  value={destinoSelecionado ? `${destinoSelecionado.tipo}-${destinoSelecionado.id}` : ''}
                  onChange={(e) => {
                    const [tipo, id] = e.target.value.split('-');
                    const destino = destinos.find(d => d.tipo === tipo && d.id === parseInt(id));
                    setDestinoSelecionado(destino || null);
                  }}
                  disabled={salvandoEncaminhar}
                >
                  <option value="">Selecione um destino...</option>
                  {destinos.map((destino, index) => (
                    <option key={index} value={`${destino.tipo}-${destino.id}`}>
                      {destino.display || `${destino.sigla} - ${destino.nome}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="observacoes-encaminhar">Observações</label>
                <textarea
                  id="observacoes-encaminhar"
                  className="form-control"
                  rows="4"
                  value={observacoesEncaminhar}
                  onChange={(e) => setObservacoesEncaminhar(e.target.value)}
                  placeholder="Informações adicionais sobre o encaminhamento..."
                  disabled={salvandoEncaminhar}
                />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={usarCertificacaoDevolutiva}
                    onChange={(e) => setUsarCertificacaoDevolutiva(e.target.checked)}
                    disabled={salvandoEncaminhar}
                  />
                  <span>✍️ Usar Certificação Digital</span>
                </label>
              </div>

              {error && <div className="alert alert-error">{error}</div>}

              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    if (!salvandoEncaminhar) {
                      setShowModalEncaminhar(false);
                      setDestinoSelecionado(null);
                      setObservacoesEncaminhar('');
                      setUsarCertificacaoDevolutiva(false);
                    }
                  }}
                  disabled={salvandoEncaminhar}
                >
                  Cancelar
                </button>
                <button
                  className="btn-primary"
                  onClick={encaminharProcesso}
                  disabled={salvandoEncaminhar || !destinoSelecionado}
                >
                  {salvandoEncaminhar ? (
                    <>
                      <span className="spinner-small"></span>
                      Encaminhando...
                    </>
                  ) : (
                    <>
                      ➡️ Encaminhar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Upload de Arquivos */}
      {showModalUpload && selectedDocumento && (
        <div className="modal-overlay" onClick={() => {
          if (!enviandoArquivos) {
            setShowModalUpload(false);
            setArquivosSelecionados([]);
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📎 Adicionar Arquivos ao Processo</h2>
              <button 
                className="btn-fechar-modal"
                onClick={() => {
                  if (!enviandoArquivos) {
                    setShowModalUpload(false);
                    setArquivosSelecionados([]);
                  }
                }}
                disabled={enviandoArquivos}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="info-section">
                <p><strong>Protocolo:</strong> {selectedDocumento.numero_protocolo}</p>
                <p><strong>Assunto:</strong> {selectedDocumento.assunto}</p>
              </div>

              <div className="form-group">
                <label htmlFor="file-input">Selecionar Arquivos (múltiplos permitidos) *</label>
                <input
                  id="file-input"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  disabled={enviandoArquivos}
                  style={{ marginBottom: '15px' }}
                />
                <p className="form-help-text">Você pode selecionar vários arquivos de uma vez (PDF, DOC, DOCX, JPG, PNG)</p>
              </div>

              {arquivosSelecionados.length > 0 && (
                <div className="arquivos-lista">
                  <h4>Arquivos Selecionados ({arquivosSelecionados.length}):</h4>
                  <div className="arquivos-grid">
                    {arquivosSelecionados.map((file, index) => (
                      <div key={index} className="arquivo-item">
                        <div className="arquivo-info">
                          <span className="arquivo-nome">{file.name}</span>
                          <span className="arquivo-tamanho">{formatarTamanhoArquivo(file.size)}</span>
                        </div>
                        <button
                          className="btn-remover-arquivo"
                          onClick={() => removerArquivo(index)}
                          disabled={enviandoArquivos}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {error && <div className="alert alert-error">{error}</div>}

              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    if (!enviandoArquivos) {
                      setShowModalUpload(false);
                      setArquivosSelecionados([]);
                    }
                  }}
                  disabled={enviandoArquivos}
                >
                  Cancelar
                </button>
                <button
                  className="btn-primary"
                  onClick={adicionarArquivos}
                  disabled={enviandoArquivos || arquivosSelecionados.length === 0}
                >
                  {enviandoArquivos ? (
                    <>
                      <span className="spinner-small"></span>
                      Enviando...
                    </>
                  ) : (
                    <>
                      📎 Adicionar {arquivosSelecionados.length > 0 ? `(${arquivosSelecionados.length})` : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal separado para visualizador de PDF */}
      {anexoVisualizando && (
        <div className="modal-overlay modal-overlay-visualizador" onClick={() => {
          setAnexoVisualizando(null);
          if (urlVisualizacao) {
            window.URL.revokeObjectURL(urlVisualizacao);
            setUrlVisualizacao(null);
          }
        }}>
          <div className="modal-content-visualizador" onClick={(e) => e.stopPropagation()}>
            <div className="visualizador-header">
              <h3>Visualizando: {anexoVisualizando.nome_original}</h3>
              <button 
                className="btn-fechar-visualizador"
                onClick={() => {
                  setAnexoVisualizando(null);
                  if (urlVisualizacao) {
                    window.URL.revokeObjectURL(urlVisualizacao);
                    setUrlVisualizacao(null);
                  }
                }}
              >
                ✕ Fechar
              </button>
            </div>
            <div className="visualizador-conteudo">
              {!urlVisualizacao ? (
                <div className="visualizador-carregando">
                  <p>Carregando arquivo...</p>
                </div>
              ) : anexoVisualizando.nome_original.toLowerCase().endsWith('.pdf') ? (
                <div className="visualizador-pdf-container">
                  {/* Botões de controle no topo */}
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    display: 'flex',
                    gap: '10px',
                    zIndex: 1000,
                    flexWrap: 'wrap'
                  }}>
                    <button
                      onClick={() => window.open(urlVisualizacao, '_blank')}
                      style={{
                        padding: '8px 16px',
                        background: '#3B82F6',
                        color: 'white',
                        border: 'none',
                        borderRadius: 0,
                        cursor: 'pointer',
                        fontSize: '14px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      title="Abrir PDF em nova aba"
                    >
                      🔗 Nova Aba
                    </button>
                    <button
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = urlVisualizacao;
                        link.download = anexoVisualizando.nome_original;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      style={{
                        padding: '8px 16px',
                        background: '#10B981',
                        color: 'white',
                        border: 'none',
                        borderRadius: 0,
                        cursor: 'pointer',
                        fontSize: '14px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      title="Baixar PDF"
                    >
                      ⬇️ Baixar
                    </button>
                  </div>
                  
                  {/* Visualização PDF - Usando embed com iframe como fallback */}
                  <div style={{ 
                    width: '100%', 
                    height: '100%', 
                    position: 'relative',
                    minHeight: '70vh'
                  }}>
                    {/* Método 1: Embed (melhor para Chrome/Firefox) */}
                    <embed
                      src={`${urlVisualizacao}#toolbar=1&navpanes=1&scrollbar=1&zoom=page-width`}
                      type="application/pdf"
                      className="visualizador-pdf-embed"
                      style={{
                        width: '100%',
                        height: '100%',
                        minHeight: '70vh',
                        border: 'none',
                        background: '#FFFFFF',
                        display: 'block'
                      }}
                    />
                    
                    {/* Método 2: Iframe (fallback para navegadores que não suportam embed bem) */}
                    <iframe
                      src={`${urlVisualizacao}#toolbar=1`}
                      title="Visualizador PDF"
                      className="visualizador-pdf-iframe"
                      style={{
                        width: '100%',
                        height: '100%',
                        minHeight: '70vh',
                        border: 'none',
                        background: '#FFFFFF',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        display: 'none' // Oculto por padrão, será mostrado se embed falhar
                      }}
                    />
                  </div>
                </div>
              ) : /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(anexoVisualizando.nome_original) ? (
                <img
                  src={urlVisualizacao}
                  alt={anexoVisualizando.nome_original}
                  className="visualizador-imagem"
                  onError={(e) => {
                    if (process.env.NODE_ENV === 'development') {
                      console.error('Erro ao carregar imagem:', e);
                    }
                    setError('Erro ao carregar a imagem.');
                  }}
                />
              ) : /\.(txt|md|json|xml|html|css|js)$/i.test(anexoVisualizando.nome_original) ? (
                <iframe
                  src={urlVisualizacao}
                  title={anexoVisualizando.nome_original}
                  className="visualizador-iframe"
                  onError={(e) => {
                    if (process.env.NODE_ENV === 'development') {
                      console.error('Erro ao carregar arquivo no iframe:', e);
                    }
                    setError('Erro ao carregar o arquivo.');
                  }}
                />
              ) : (
                <div className="visualizador-nao-suportado">
                  <p>Este tipo de arquivo não pode ser visualizado no navegador.</p>
                  <p>Por favor, use o botão "Baixar" para abrir o arquivo.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Protocolos;
